import { NotificationType, useTranslatedVoiceSampleScript, useTranslation, useUserVoices } from "@next-platform/client-common-core";
import { useCallback, useEffect } from "react";

import useToast from "@/hooks/useToast";
import useVoicePreview from "@/pages/VoiceClonePreview/useVoicePreview";
import { copyToClipboard } from "@/utils";

import { UseVoiceDetailReturn } from "../types";

export function useVoiceDetail(voiceId: string, stateIsEditing: boolean): UseVoiceDetailReturn {
  const { getUserVoiceById, updateUserVoice, isLoading, voice } = useUserVoices();
  const { getTranslatedVoiceSampleScript } = useTranslatedVoiceSampleScript();
  const { t } = useTranslation();

  useEffect(() => {
    void getUserVoiceById(voiceId);
  }, [voiceId, stateIsEditing, getUserVoiceById]);

  const { displayToast } = useToast();
  const sampleText = getTranslatedVoiceSampleScript(voice?.language);

  const { previewVoice, stopPreview, isGenerating, isPlaying } = useVoicePreview(voice?.id);

  const handleSpeak = useCallback(() => previewVoice(sampleText), [previewVoice, sampleText]);

  const handleStop = useCallback(() => stopPreview(), [stopPreview]);

  const handleCopyToClipboard = useCallback((text: string, label: string) => copyToClipboard(text, label, displayToast), [displayToast]);

  const updateVoice = useCallback(
    async (payload: { name: string; description: string }): Promise<boolean> => {
      if (!voiceId) return false;
      try {
        await updateUserVoice({
          voice_id: voiceId,
          name: payload.name,
          description: payload.description,
        });
        displayToast({ message: t("voice:form.edit.toast.updateSuccess"), type: NotificationType.success });
        return true;
      } catch {
        displayToast({ message: t("voice:form.edit.toast.updateError.message"), type: NotificationType.error });
        return false;
      }
    },
    [voiceId, displayToast, updateUserVoice, t],
  );

  return {
    voice: voice,
    language: voice?.language || "EN",
    isLoading,
    isAudioLoading: isGenerating,
    sampleText,
    isPlaying,
    handleSpeak,
    handleStop,
    copyToClipboard: handleCopyToClipboard,
    updateVoice,
  };
}
